import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { getNewsById, getNewsList, getNewsCategories } from "@/api/newsApi";
import { assetUrl } from "@/utils/assetUrl";

const NEWS_ACCENT = "#64748b";

const categoryColorMap: Record<string, { bg: string; text: string; border: string }> = {
  industry: { bg: "bg-[#64748b]/8", text: "text-[#64748b]", border: "border-[#64748b]/20" },
  tech: { bg: "bg-[#64748b]/8", text: "text-[#64748b]", border: "border-[#64748b]/20" },
  policy: { bg: "bg-[#64748b]/8", text: "text-[#64748b]", border: "border-[#64748b]/20" },
  company: { bg: "bg-[#64748b]/8", text: "text-[#64748b]", border: "border-[#64748b]/20" },
  security: { bg: "bg-[#64748b]/8", text: "text-[#64748b]", border: "border-[#64748b]/20" },
};

interface NewsItem {
  id: number;
  category: string;
  categoryName: string;
  title: string;
  summary: string;
  content: string;
  coverImage: string;
  tags: string[];
  publishDate: string;
  readTime: string;
}

interface NewsCategory {
  id: string;
  name: string;
}

function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, "").replace(/&[a-z]+;/gi, " ");
}

function calcReadTime(content: string): string {
  const text = stripHtml(content);
  const chars = text.replace(/\s/g, "").length;
  const minutes = Math.max(1, Math.ceil(chars / 200));
  return `${minutes}分钟`;
}

export default function NewsDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const heroRef = useRef<HTMLElement>(null);

  const [newsData, setNewsData] = useState<NewsItem | null>(null);
  const [relatedNews, setRelatedNews] = useState<NewsItem[]>([]);
  const [otherNews, setOtherNews] = useState<NewsItem[]>([]);
  const [categories, setCategories] = useState<NewsCategory[]>([]);
  const [allNews, setAllNews] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
    setLoading(true);
    setError("");

    const newsId = Number(id);
    if (!newsId) {
      setError("无效的新闻ID");
      setLoading(false);
      return;
    }

    Promise.all([
      getNewsById(newsId),
      getNewsList({ pageNum: 1, pageSize: 20 }),
      getNewsCategories(),
    ])
      .then(([detailRes, listRes, catRes]: any[]) => {
        const detail = detailRes;
        if (!detail || !detail.id) {
          setError("资讯不存在");
          return;
        }
        setNewsData(detail);

        const list: NewsItem[] = (listRes.rows || []);
        setAllNews(list);
        setRelatedNews(list.filter((n) => n.category === detail.category && n.id !== detail.id).slice(0, 3));
        setOtherNews(list.filter((n) => n.id !== detail.id).slice(0, 5));
        setCategories(Array.isArray(catRes) ? catRes : []);
      })
      .catch(() => {
        setError("加载新闻详情失败，请稍后重试");
      })
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const heroHeight = heroRef.current?.offsetHeight ?? 400;
  const showFloatingBack = scrollY > heroHeight - 72;
  const showBackToTop = scrollY > 500;
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  if (loading) {
    return (
      <main className="min-h-screen bg-[#f8fafc] flex items-center justify-center">
        <div className="text-gray-400 text-sm flex items-center gap-2">
          <i className="ri-loader-4-line animate-spin text-lg"></i>加载中...
        </div>
      </main>
    );
  }

  if (error || !newsData) {
    return (
      <main className="min-h-screen bg-[#f8fafc] flex items-center justify-center">
        <div className="text-center">
          <i className="ri-error-warning-line text-4xl text-gray-300 block mb-3"></i>
          <p className="text-gray-400 mb-4">{error || "资讯不存在"}</p>
          <button onClick={() => navigate("/news")} className="px-6 py-2 bg-[#0d1f3c] text-white rounded-lg text-sm cursor-pointer whitespace-nowrap">
            返回资讯列表
          </button>
        </div>
      </main>
    );
  }

  const catStyle = categoryColorMap[newsData.category] || { bg: "bg-gray-50", text: "text-gray-600", border: "border-gray-100" };

  return (
    <main className="min-h-screen bg-[#f8fafc]">
      {/* Fixed Back Button */}
      <button
        onClick={() => navigate("/news")}
        className={`fixed left-6 z-50 w-11 h-11 flex items-center justify-center rounded-full text-white transition-all duration-300 cursor-pointer ${
          showFloatingBack ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-2 pointer-events-none"
        }`}
        style={{ top: "100px", backgroundColor: "#0d1f3c", boxShadow: "0 4px 16px rgba(13,31,60,0.4)" }}
        aria-label="返回资讯列表"
      >
        <i className="ri-arrow-left-line text-xl"></i>
      </button>

      {/* Fixed Back to Top Button */}
      <button
        onClick={scrollToTop}
        className={`fixed right-5 z-50 w-12 h-12 flex items-center justify-center rounded-full text-white transition-all duration-300 cursor-pointer ${
          showBackToTop ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"
        }`}
        style={{ backgroundColor: "#0d1f3c", bottom: "96px", boxShadow: "0 4px 16px rgba(13,31,60,0.4)" }}
        aria-label="返回顶部"
      >
        <i className="ri-arrow-up-line text-lg"></i>
      </button>

      {/* Hero Section */}
      <section ref={heroRef} className="relative bg-[#0d1f3c] pt-24 pb-16 overflow-hidden">
        <div className="absolute inset-0">
          <img src={assetUrl(newsData.coverImage)} alt="" className="w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0d1f3c]/95 via-[#0d1f3c]/85 to-[#0d1f3c]/70"></div>
        </div>
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 items-start">
            <div className="flex-1 min-w-0">
              <button
                onClick={() => navigate("/news")}
                className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 border border-white/20 rounded-full text-white text-sm transition-all duration-200 cursor-pointer mb-6"
              >
                <div className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-left-line"></i></div>
                返回资讯列表
              </button>
              <div className="flex flex-wrap items-center gap-4 mb-6">
                <span className="px-4 py-1.5 bg-white/15 border border-white/25 text-white text-sm font-medium rounded-full">
                  {newsData.categoryName}
                </span>
                <span className="flex items-center gap-2 text-white/70 text-sm">
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-calendar-line"></i></div>
                  {newsData.publishDate}
                </span>
                <span className="flex items-center gap-2 text-white/70 text-sm">
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-time-line"></i></div>
                  阅读约 {newsData.readTime || calcReadTime(newsData.content)}
                </span>
              </div>
              <h1 className="text-3xl lg:text-4xl xl:text-5xl font-bold text-white leading-tight mb-6">{newsData.title}</h1>
              <p className="text-base lg:text-lg text-white/80 leading-relaxed max-w-4xl mb-8">{newsData.summary}</p>
              <div className="flex flex-wrap items-center gap-2">
                {(newsData.tags || []).map((tag) => (
                  <span key={tag} className="px-3 py-1 bg-white/10 text-white/90 text-sm rounded-full border border-white/20">#{tag}</span>
                ))}
              </div>
            </div>
            <div className="w-full lg:w-[420px] xl:w-[480px] flex-shrink-0">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img src={assetUrl(newsData.coverImage)} alt={newsData.title} className="w-full h-[280px] lg:h-[320px] object-cover object-top" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Content Area */}
      <section className="py-12 lg:py-16">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="flex flex-col lg:flex-row gap-10">
            {/* Left: Article */}
            <article className="flex-1 min-w-0">
              <div className="bg-white rounded-xl border border-gray-100 p-7 lg:p-10 mb-8">
                <div
                  className="prose prose-sm max-w-none text-gray-600 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: newsData.content }}
                />
              </div>

              {/* Share Bar */}
              <div className="flex items-center justify-between py-5 border-t border-b border-gray-100 mb-10">
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-share-line"></i></div>
                  分享本文
                </div>
                <div className="flex items-center gap-3">
                  {[
                    { icon: "ri-wechat-line", label: "微信" },
                    { icon: "ri-weibo-line", label: "微博" },
                    { icon: "ri-links-line", label: "复制链接" },
                  ].map((item) => (
                    <button key={item.label} className="flex items-center gap-1.5 px-4 py-2 bg-gray-50 hover:bg-gray-100 text-gray-600 text-sm rounded-lg transition-colors cursor-pointer whitespace-nowrap border border-gray-100">
                      <div className="w-4 h-4 flex items-center justify-center"><i className={item.icon}></i></div>
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Related Articles */}
              {relatedNews.length > 0 && (
                <div>
                  <h2 className="text-lg font-bold text-[#0d1f3c] mb-5">相关资讯</h2>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {relatedNews.map((n) => {
                      const s = categoryColorMap[n.category] || { bg: "bg-gray-50", text: "text-gray-600", border: "border-gray-100" };
                      return (
                        <Link key={n.id} to={`/news/${n.id}`} className="group bg-white rounded-xl border border-gray-100 overflow-hidden hover:border-gray-200 hover:-translate-y-1 transition-all duration-300 cursor-pointer">
                          <div className="w-full h-36 overflow-hidden">
                            <img src={assetUrl(n.coverImage)} alt={n.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                          </div>
                          <div className="p-4">
                            <div className="flex items-center gap-2 mb-2">
                              <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${s.bg} ${s.text} border ${s.border}`}>{n.categoryName}</span>
                              <span className="text-gray-400 text-xs">{n.publishDate}</span>
                            </div>
                            <h3 className="text-sm font-bold text-[#0d1f3c] line-clamp-2 leading-snug">{n.title}</h3>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              )}
            </article>

            {/* Right: Sidebar */}
            <aside className="w-full lg:w-72 flex-shrink-0">
              <div className="bg-white rounded-xl border border-gray-100 p-6 mb-5 sticky top-[80px]">
                <h3 className="text-sm font-bold text-[#0d1f3c] mb-4 pb-3 border-b border-gray-100">文章信息</h3>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0" style={{ backgroundColor: `${NEWS_ACCENT}15` }}>
                      <i className="ri-price-tag-3-line text-sm" style={{ color: NEWS_ACCENT }}></i>
                    </div>
                    <div><div className="text-xs text-gray-400">分类</div><div className="text-sm font-medium text-[#0d1f3c]">{newsData.categoryName}</div></div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0" style={{ backgroundColor: `${NEWS_ACCENT}15` }}>
                      <i className="ri-calendar-line text-sm" style={{ color: NEWS_ACCENT }}></i>
                    </div>
                    <div><div className="text-xs text-gray-400">发布日期</div><div className="text-sm font-medium text-[#0d1f3c]">{newsData.publishDate}</div></div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0" style={{ backgroundColor: `${NEWS_ACCENT}15` }}>
                      <i className="ri-time-line text-sm" style={{ color: NEWS_ACCENT }}></i>
                    </div>
                    <div><div className="text-xs text-gray-400">阅读时长</div><div className="text-sm font-medium text-[#0d1f3c]">{newsData.readTime || calcReadTime(newsData.content)}</div></div>
                  </div>
                </div>
                <div className="mt-5 pt-4 border-t border-gray-100">
                  <p className="text-xs text-gray-400 mb-3 leading-relaxed">需要了解更多安全解决方案？</p>
                  <a href="/#contact-form" className="block w-full py-2.5 text-center text-sm font-semibold text-white rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer" style={{ backgroundColor: NEWS_ACCENT }}>
                    联系我们
                  </a>
                </div>
              </div>

              {/* Categories */}
              <div className="bg-white rounded-xl border border-gray-100 p-5 mb-5">
                <h3 className="text-sm font-bold text-[#0d1f3c] mb-3 pb-3 border-b border-gray-100">资讯分类</h3>
                <div className="space-y-1">
                  {categories.filter(c => c.id !== "all").map((cat) => {
                    const count = allNews.filter(n => n.category === cat.id).length;
                    const s = categoryColorMap[cat.id] || { bg: "bg-gray-50", text: "text-gray-600", border: "border-gray-100" };
                    const isActive = cat.id === newsData.category;
                    return (
                      <Link key={cat.id} to="/news" className={`flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors cursor-pointer ${isActive ? `${s.bg} ${s.text} font-medium` : "text-gray-500 hover:bg-gray-50"}`}>
                        <span>{cat.name}</span>
                        <span className={`text-xs px-1.5 py-0.5 rounded-full ${isActive ? `${s.bg} ${s.text}` : "bg-gray-100 text-gray-400"}`}>{count}</span>
                      </Link>
                    );
                  })}
                </div>
              </div>

              {/* Latest Articles */}
              <div className="bg-white rounded-xl border border-gray-100 p-5">
                <h3 className="text-sm font-bold text-[#0d1f3c] mb-3 pb-3 border-b border-gray-100">最新资讯</h3>
                <div className="space-y-3">
                  {otherNews.map((n) => (
                    <Link key={n.id} to={`/news/${n.id}`} className="flex gap-3 group cursor-pointer">
                      <div className="w-16 h-12 rounded-lg overflow-hidden flex-shrink-0">
                        <img src={assetUrl(n.coverImage)} alt={n.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-[#0d1f3c] line-clamp-2 leading-snug group-hover:text-[#1a2f52] transition-colors">{n.title}</p>
                        <p className="text-xs text-gray-400 mt-1">{n.publishDate}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </main>
  );
}
